import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  ButtonGroup,
  ModalFooter,
} from '@chakra-ui/react';
import { useRef } from 'react';
import { Camera, CameraType } from 'react-camera-pro';

import { WebcamOverlay } from './components/WebcamOverlay';

interface IWebcamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}

export const WebcamModal = ({
  isOpen,
  onClose,
  onCapture,
}: IWebcamModalProps): JSX.Element => {
  const webcamRef = useRef<CameraType>(null);

  const handleCapture = (): void => {
    const fileBase64 = webcamRef.current?.takePhoto('base64url').toString();

    if (fileBase64) {
      const [fileType, base64Data] = fileBase64.split(',');

      const mimeType = fileType.match(/:(.*?);/)?.[1] ?? 'image/jpeg';

      const binaryString = atob(base64Data);

      const bytes = new Uint8Array(binaryString.length);

      bytes.forEach((_, index) => {
        bytes[index] = binaryString.charCodeAt(index);
      });

      const blob = new Blob([bytes], { type: mimeType });

      const fileExt = mimeType.match(/\/(.+)/)?.[1] ?? '.jpeg';

      const fileName = String(
        crypto.getRandomValues(new Uint32Array(1)),
      ).concat(`.${fileExt}`);

      const file = new File([blob], fileName, {
        type: mimeType,
      });

      onCapture(file);
    }
  };

  return (
    <Modal size="lg" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />

      <ModalContent h={792} w={480}>
        <ModalBody
          p={0}
          bg="gray.300"
          position="relative"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Camera
            ref={webcamRef}
            aspectRatio={2 / 3}
            facingMode="user"
            errorMessages={{
              noCameraAccessible:
                'Nenhum dispositivo de câmera acessível. Por favor conecte sua câmera ou tente um navegador diferente.',
              permissionDenied:
                'Permissão negada. Por favor atualize a página e dê permissão para câmera.',
              switchCamera:
                'Não é possível de trocar as câmeras porque só existe um dispositivo acessível.',
              canvas: 'Canvas não suportado.',
            }}
          />

          <WebcamOverlay />
        </ModalBody>

        <ModalFooter p={0} bg="black">
          <ButtonGroup justifyContent="center" w="100%" py="4">
            <Button colorScheme="blackAlpha" onClick={onClose}>
              Cancelar
            </Button>

            <Button colorScheme="green" onClick={handleCapture}>
              Capturar
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
